import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Task } from '../../types/tasks';
import { updateTask } from '../../features/tasks/taskSlice';
import { useFormValidation } from '../../hooks/useFormValidation';
import Card from '../ui/Card';
import Button from '../ui/Button';

interface Props {
  task: Task;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditTaskForm: React.FC<Props> = ({ task, onCancel, onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { values, errors, handleChange, handleSubmit, isValid } = useFormValidation(
    {
      title: task.title,
      description: task.description || '',
      due_date: task.due_date || '',
      status: task.status,
    },
    {
      title: (value) => (!value ? t('tasks.validation.titleRequired') : ''),
      due_date: (value) => {
        if (!value) return '';
        if (new Date(value) < new Date(new Date().setHours(0, 0, 0, 0))) {
          return t('tasks.validation.dueDatePast');
        }
        return '';
      },
    },
    async (values) => {
      await dispatch(updateTask({ id: task.id, ...values }));
      onSuccess();
    }
  );

  return (
    <Card>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
            {t('tasks.taskTitle')}
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={values.title}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          />
          {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            {t('tasks.description')}
          </label>
          <textarea
            id="description"
            name="description"
            rows={3}
            value={values.description}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="due_date" className="block text-sm font-medium text-gray-700">
            {t('tasks.dueDate')}
          </label>
          <input
            type="date"
            id="due_date"
            name="due_date"
            value={values.due_date}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          />
          {errors.due_date && (
            <p className="mt-1 text-sm text-red-600">{errors.due_date}</p>
          )}
        </div>

        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700">
            {t('common.status')}
          </label>
          <select
            id="status"
            name="status"
            value={values.status}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          >
            <option value="pending">{t('tasks.status.pending')}</option>
            <option value="in_progress">{t('tasks.status.in_progress')}</option>
            <option value="completed">{t('tasks.status.completed')}</option>
          </select>
        </div>

        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={!isValid}
          >
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default EditTaskForm;